import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { keys } from '@env/shared-environment/keys';
import { DialogRef, DialogService } from '@shared/components/dialog';
import { EitherApi } from '@shared/services/either-api/either-api';
import { HttpCacheKeys, HttpCacheTtl } from '@shared/services/http-cache/http-cache.keys';
import { HttpCacheService } from '@shared/services/http-cache/http-cache.service';
import { LocalStorage } from '@shared/services/local-storage/local-storage.service';
import { catchError, firstValueFrom, of } from 'rxjs';
import { Either } from '../../../../helpers/either';
import { LegalDocumentDialogComponent } from '../../components/legal-document-dialog.component';
import { ConsentAcceptancePayload, ConsentStatusDto, DEFAULT_LEGAL_DOCUMENTS, LegalDocumentDto, LegalSlug, REQUIRED_CONSENT_ACCEPTANCES_COUNT } from '../../legal.models';

interface CookieNoticeAck {
  acknowledgedAt: string;
}

@Injectable({
  providedIn: 'root'
})
export class LegalService {
  private readonly eitherApi = new EitherApi();

  private readonly assetHttp = new HttpClient(inject(HttpBackend));

  private readonly dialogService = inject(DialogService);

  private readonly localStorage = inject(LocalStorage);

  private readonly cache = inject(HttpCacheService);

  public loadDocuments(): Promise<LegalDocumentDto[]> {
    return this.cache.get(
      HttpCacheKeys.legalDocuments,
      async () => {
        const response = await this.eitherApi.get<LegalDocumentDto[]>(environment.routes.legal.documents, true);

        if (response.isRight() && response.value.length >= REQUIRED_CONSENT_ACCEPTANCES_COUNT) {
          return response.value;
        }

        return DEFAULT_LEGAL_DOCUMENTS;
      },
      { ttl: HttpCacheTtl.legalDocuments }
    );
  }

  public buildAcceptances(documents: LegalDocumentDto[]): ConsentAcceptancePayload[] {
    return documents.map(document => ({
      documentType: document.type,
      documentVersion: document.version
    }));
  }

  public async getStatus(): Promise<Either<HttpErrorResponse, ConsentStatusDto>> {
    return this.eitherApi.get<ConsentStatusDto>(environment.routes.legal.status, true, { withCredentials: true });
  }

  public async accept(acceptances: ConsentAcceptancePayload[]): Promise<Either<HttpErrorResponse, void>> {
    return this.eitherApi.post<void>(environment.routes.legal.accept, { acceptances }, true, { withCredentials: true });
  }

  public async acceptCookies(): Promise<Either<HttpErrorResponse, void>> {
    return this.accept([{ documentType: 'cookie_policy', documentVersion: '1.0.0' }]);
  }

  public loadMarkdown(path: string): Promise<string> {
    const url = path.startsWith('/') ? path : `/${path}`;

    return this.cache.get(
      HttpCacheKeys.legalMarkdown(url),
      async () => {
        try {
          return await firstValueFrom(this.assetHttp.get(url, { responseType: 'text' }).pipe(catchError(() => of(''))));
        } catch {
          return '';
        }
      },
      { ttl: HttpCacheTtl.legalMarkdown }
    );
  }

  public hasCookieNoticeAcknowledged(): boolean {
    return this.localStorage.getItem<CookieNoticeAck>(keys.cookieNotice) !== null;
  }

  public acknowledgeCookieNotice(): void {
    this.localStorage.setItem<CookieNoticeAck>(keys.cookieNotice, {
      acknowledgedAt: new Date().toISOString()
    });
  }

  public openDocument(slug: LegalSlug): DialogRef<LegalDocumentDialogComponent> {
    return this.dialogService.create({
      content: LegalDocumentDialogComponent,
      position: 'center',
      animation: 'opacity',
      closable: true,
      data: { slug }
    });
  }

  public openTerms(): DialogRef<LegalDocumentDialogComponent> {
    return this.openDocument('terms');
  }

  public openPrivacy(): DialogRef<LegalDocumentDialogComponent> {
    return this.openDocument('privacy');
  }

  public openCookies(): DialogRef<LegalDocumentDialogComponent> {
    return this.openDocument('cookies');
  }
}
